import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { colors, cardStyle } from '../theme';
import type { ApplicationSummary } from '../types';

/**
 * Confirmation step for a destructive action, deliberately not `window.confirm`
 * — that can't be styled, can't be asserted on in a test, and gives no room to
 * state precisely what survives the delete, which is the whole point here.
 *
 * Rendered through a portal for the same reason RowActions is (see the note in
 * ApplicationsTable.tsx): the trigger lives inside the table's
 * `overflowX: auto` wrapper, and anything positioned from in there gets clipped.
 */
export function DeleteApplicationDialog({
  apps,
  busy,
  progress,
  error,
  onCancel,
  onConfirm,
}: {
  /** One app for a row's own Delete action, several for a bulk "Delete
   * selected" action — same dialog either way, just different copy. */
  apps: ApplicationSummary[];
  busy: boolean;
  /** How far a bulk delete has gotten (`done` includes both successes and
   * failures) — undefined/omitted for a single-app delete, which has no
   * meaningful partial state to show. */
  progress?: { done: number; total: number };
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const confirmRef = useRef<HTMLButtonElement>(null);
  const isBulk = apps.length > 1;

  // Escape to cancel, and focus starts on the dialog rather than wherever the
  // now-closed row menu left it.
  useEffect(() => {
    confirmRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);

  if (apps.length === 0) return null;

  return createPortal(
    <>
      <div
        onClick={() => !busy && onCancel()}
        style={{ position: 'fixed', inset: 0, background: 'rgba(16, 24, 40, 0.45)', zIndex: 200 }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={isBulk ? `Delete ${apps.length} applications` : `Delete ${apps[0].name}`}
        style={{
          ...cardStyle,
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'calc(100% - 32px)',
          maxWidth: 440,
          padding: 24,
          zIndex: 201,
        }}
      >
        <h2 style={{ fontSize: 17, fontWeight: 700, color: colors.text, margin: '0 0 10px' }}>
          {isBulk ? `Delete ${apps.length} applications?` : `Delete ${apps[0].name}?`}
        </h2>

        {isBulk && (
          <ul
            style={{
              margin: '0 0 12px',
              padding: '0 0 0 18px',
              fontSize: 13,
              color: colors.text,
              maxHeight: 120,
              overflowY: 'auto',
            }}
          >
            {apps.map((a) => (
              <li key={a.id}>{a.name}</li>
            ))}
          </ul>
        )}

        <p style={{ fontSize: 14, color: colors.textMuted, margin: '0 0 8px', lineHeight: 1.5 }}>
          This stops and removes {isBulk ? 'these running applications' : 'the running application'}.
          {isBulk ? ' They will' : ' It will'} disappear from the platform and{' '}
          {isBulk ? 'their URLs will' : 'its URL will'} stop responding.
        </p>
        <p style={{ fontSize: 14, color: colors.textMuted, margin: '0 0 20px', lineHeight: 1.5 }}>
          {isBulk ? 'Their' : 'Its'} GitHub repositor{isBulk ? 'ies are' : 'y is'}{' '}
          <strong style={{ color: colors.text }}>not</strong> deleted, so{' '}
          {isBulk ? 'they can' : 'the app can'} be deployed again from the same source.
        </p>

        {busy && progress && isBulk && (
          <div style={{ fontSize: 13, color: colors.textMuted, marginBottom: 16 }}>
            Deleting {progress.done} of {progress.total}…
          </div>
        )}

        {error && (
          <div role="alert" style={{ fontSize: 13, color: colors.danger, marginBottom: 16, whiteSpace: 'pre-wrap' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            style={{
              padding: '9px 16px',
              borderRadius: 8,
              border: `1px solid ${colors.border}`,
              background: colors.surface,
              color: colors.text,
              fontSize: 14,
              fontWeight: 600,
              cursor: busy ? 'default' : 'pointer',
              opacity: busy ? 0.6 : 1,
            }}
          >
            {error ? 'Close' : 'Cancel'}
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={onConfirm}
            disabled={busy}
            aria-label={isBulk ? `Confirm delete ${apps.length} applications` : `Confirm delete ${apps[0].name}`}
            style={{
              padding: '9px 16px',
              borderRadius: 8,
              border: 'none',
              background: busy ? colors.borderLight : colors.danger,
              color: busy ? colors.textMuted : '#fff',
              fontSize: 14,
              fontWeight: 600,
              cursor: busy ? 'default' : 'pointer',
            }}
          >
            {busy ? 'Deleting…' : isBulk ? `Delete ${apps.length} applications` : 'Delete application'}
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
}
